"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RedemptionDetailsSchema = exports.WalletCouponItemSchema = exports.getWalletCouponDetailsResponseSchema = exports.getWalletCouponDetailsParamsSchema = exports.listWalletCouponsResponseSchema = exports.listWalletCouponsQuerySchema = exports.UserCouponEntitySchema = exports.CouponEntitySchema = exports.CouponSourceSchema = exports.UserCouponStatusSchema = exports.CouponTypeSchema = void 0;
const zod_1 = require("zod");
const common_schemas_1 = require("../base/common.schemas");
const api_schemas_1 = require("../base/api.schemas");
const timestamp_schemas_1 = require("../base/timestamp.schemas");
// ============================================
// INTERNAL SCHEMAS (Entity)
// ============================================
// Coupon discount types (from coupons.coupon_type CHECK constraint)
const _CouponTypeSchema = zod_1.z.enum(['percentage', 'fixed_amount', 'free_item', 'bogo']);
// User coupon instance status (from user_coupons.status CHECK constraint)
const _UserCouponStatusSchema = zod_1.z.enum(['active', 'expired']);
// Coupon acquisition source (from user_coupons.source CHECK constraint)
const _CouponSourceSchema = zod_1.z.enum(['direct', 'shared', 'distribution', 'reward']);
// User coupon entity schema matching database user_coupons table
const _UserCouponBaseSchema = zod_1.z.object({
    user_coupon_id: zod_1.z.number().int().positive(),
    user_id: common_schemas_1.uuidSchema,
    coupon_id: zod_1.z.number().int().positive(),
    code: zod_1.z.string().max(10),
    phone_number: zod_1.z.string().max(20).nullable(),
    source: _CouponSourceSchema,
    source_sharing_id: zod_1.z.number().int().positive().nullable(),
    source_user_id: common_schemas_1.uuidSchema.nullable(),
    status: _UserCouponStatusSchema,
    used_at: timestamp_schemas_1.timestampIsoSchema.nullable(),
    claimed_at: timestamp_schemas_1.timestampIsoSchema.nullable(),
    created_at: timestamp_schemas_1.timestampIsoSchema,
});
// Coupon base schema matching database coupons table
const _CouponBaseSchema = zod_1.z.object({
    coupon_id: zod_1.z.number().int().positive(),
    merchant_id: zod_1.z.number().int().positive(),
    title: zod_1.z.string().max(200),
    description: zod_1.z.string(),
    coupon_type: _CouponTypeSchema,
    coupon_value: zod_1.z.number().int().min(0), // Percentage (0-100) or fixed discount in cents
    text_value: zod_1.z.string().max(500).nullable(), // For free_item/bogo descriptions
    minimum_amount: common_schemas_1.moneyAmountSchema.nullable(), // Minimum purchase required (cents)
    commission_basis_points: common_schemas_1.basisPointsSchema.default(0),
    is_active: zod_1.z.boolean().default(true),
    max_redemptions: zod_1.z.number().int().positive().nullable(),
    current_redemptions: zod_1.z.number().int().min(0).default(0),
    starts_at: timestamp_schemas_1.timestampIsoSchema.nullable(),
    expires_at: timestamp_schemas_1.timestampIsoSchema.nullable(),
    // Visual design fields (FR178-180)
    background_image_url: zod_1.z.string().url().max(500).nullable(),
    // Multi-language support (FR140-141, FR183)
    title_fr: zod_1.z.string().min(2).max(200).nullable(),
    description_fr: zod_1.z.string().min(2).max(500).nullable(),
    title_zh: zod_1.z.string().min(2).max(200).nullable(),
    description_zh: zod_1.z.string().min(2).max(500).nullable(),
    // Distribution tracking (FR35 - locked after distribution)
    distributed_at: timestamp_schemas_1.timestampIsoSchema.nullable(),
    created_at: timestamp_schemas_1.timestampIsoSchema,
    updated_at: timestamp_schemas_1.timestampIsoSchema,
});
// ============================================
// ENTITY SCHEMAS (Export Aliases)
// ============================================
exports.CouponTypeSchema = _CouponTypeSchema;
exports.UserCouponStatusSchema = _UserCouponStatusSchema;
exports.CouponSourceSchema = _CouponSourceSchema;
exports.CouponEntitySchema = _CouponBaseSchema;
exports.UserCouponEntitySchema = _UserCouponBaseSchema;
// ============================================
// CUSTOMER WALLET API SCHEMAS
// ============================================
// Internal: Merchant summary for wallet view
const _MerchantSummarySchema = zod_1.z.object({
    merchant_id: zod_1.z.number().int(),
    merchant_name: zod_1.z.string(),
    logo_url: zod_1.z.string().url().nullable(),
});
// Internal: Redemption details for redeemed coupon instances (Story 7.9)
const _RedemptionDetailsSchema = zod_1.z
    .object({
    redeemed_at: timestamp_schemas_1.timestampIsoSchema,
    discount_amount: common_schemas_1.moneyAmountSchema, // From coupon_redemptions.discount_amount
    final_amount: common_schemas_1.moneyAmountSchema.nullable(), // What customer paid (bill - discount)
    merchant_name: zod_1.z.string().nullable(), // Merchant where redeemed (for display)
})
    .nullable();
// Internal: Wallet coupon item (list view)
const _WalletCouponItemSchema = zod_1.z.object({
    user_coupon_id: zod_1.z.number().int(),
    code: zod_1.z.string(),
    status: exports.UserCouponStatusSchema,
    source: exports.CouponSourceSchema,
    coupon: _CouponBaseSchema.pick({
        coupon_id: true,
        title: true,
        title_fr: true,
        description: true,
        description_fr: true,
        description_zh: true,
        title_zh: true,
        coupon_type: true,
        coupon_value: true,
        minimum_amount: true,
        commission_basis_points: true,
        expires_at: true,
        background_image_url: true, // For coupon card background display (Story 5.3)
    }),
    merchant: _MerchantSummarySchema,
    created_at: timestamp_schemas_1.timestampIsoSchema,
    // Sharing info (Story 5.4) - nullable until Epic 6 backend implemented
    shared_to: zod_1.z.string().nullable(), // Masked phone: "+1 ***-1234"
    shared_at: timestamp_schemas_1.timestampIsoSchema.nullable(), // When shared
    claimed_at: timestamp_schemas_1.timestampIsoSchema.nullable(), // When claimed (null = waiting)
    share_expires_at: timestamp_schemas_1.timestampIsoSchema.nullable(), // 48h from shared_at
    // Share analytics (Story 6.4) - aggregated at coupon level
    share_count: zod_1.z.number().int().default(0), // Number of times shared
    total_views: zod_1.z.number().int().default(0), // Total views across all shares
    // Redemption details (Story 7.9) - populated when redemption history exists
    redemption_details: _RedemptionDetailsSchema,
    // Source user info for shared coupons (Story 7.9 AC #5)
    source_user_name: zod_1.z.string().nullable(), // Who shared this coupon to me
});
// GET /wallet/coupons - Query
exports.listWalletCouponsQuerySchema = zod_1.z.object({
    merchantId: zod_1.z.coerce.number().int().positive().optional(), // Filter by merchant
    status: exports.UserCouponStatusSchema.optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(50),
    cursor: zod_1.z.string().optional(),
});
// GET /wallet/coupons - Response
exports.listWalletCouponsResponseSchema = (0, api_schemas_1.listResponseSchema)(_WalletCouponItemSchema);
// GET /wallet/coupons/:instanceId - Params
exports.getWalletCouponDetailsParamsSchema = zod_1.z.object({
    instanceId: zod_1.z.coerce.number().int().positive(),
});
// GET /wallet/coupons/:instanceId - Response (extended details)
// Extends wallet item with additional coupon details from entity
exports.getWalletCouponDetailsResponseSchema = (0, api_schemas_1.dataResponseSchema)(_WalletCouponItemSchema.extend({
    // Additional coupon details from entity
    description: _CouponBaseSchema.shape.description,
    minimum_amount: _CouponBaseSchema.shape.minimum_amount,
    commission_basis_points: _CouponBaseSchema.shape.commission_basis_points,
    // User coupon specific fields (legacy compatibility)
    used_at: timestamp_schemas_1.timestampIsoSchema.nullable(),
    // Sharing info if shared
    source_user_name: zod_1.z.string().nullable(), // Who shared it to me
}));
// Export for reuse
exports.WalletCouponItemSchema = _WalletCouponItemSchema;
exports.RedemptionDetailsSchema = _RedemptionDetailsSchema;
